import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import settings from "@/settings.json";
import { API_ENDPOINTS } from "@/constants/apiConstants";
import { DSPlayerOBj, DSSplitContentGroup, DSUpdateResponse, PlaylistResponseItem } from "./types";
import { encrypt } from "@/lib/encryptUtils";
import { formatInTimeZone } from "date-fns-tz";
import { PlayerSyncStatus } from "@/lib/types";

const dsApi = axios.create({
  baseURL: settings.Server_Url,
});

const axiosConfig: AxiosRequestConfig = {
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
    Accept: "*/*",
  },
};

export const getDsList = (
  values:
    | {
        uniqueId: string;
      }
    | { contentGroupId: string }
): Promise<AxiosResponse<PlaylistResponseItem[] | [DSSplitContentGroup] | DSSplitContentGroup>> => {
  const formData = new FormData();
  if ("uniqueId" in values) {
    formData.append("UniqueId", values.uniqueId);
  } else {
    formData.append("ContentGroupId", values.contentGroupId);
  }

  return dsApi.put(API_ENDPOINTS.DS.GET_DS_LIST, formData, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      context: encrypt(formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd HH:mm:ss")),
    },
  });
};

export const getUpdate = async (uniqueId: string, code: string): Promise<DSUpdateResponse | null> => {
  const formData = new FormData();
  formData.append("UniqueId", uniqueId);
  formData.append("Code", code);

  const isOnline = navigator.onLine;
  let data: DSUpdateResponse | null = null;

  if (isOnline) {
    const response = await dsApi.put(API_ENDPOINTS.DS.GET_UPDATE, formData, {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        context: encrypt(formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd HH:mm:ss")),
      },
    });
    data = response.data;
  } else {
    const cachedData = localStorage.getItem(`dsUpdate-${uniqueId}`);
    if (cachedData) {
      data = JSON.parse(cachedData);
    }
  }

  return data;
};

export const getDsProcess = async (uniqueId: string, code: string): Promise<DSPlayerOBj<string> | null> => {
  const formData = new FormData();
  formData.append("UniqueId", uniqueId);
  formData.append("Code", code);

  const isOnline = navigator.onLine;
  let data: DSPlayerOBj<string> | null = null;

  if (isOnline) {
    const response = await dsApi.put(API_ENDPOINTS.DS.GET_DS_PROCESS, formData, {
      ...axiosConfig,
      headers: {
        ...axiosConfig.headers,
        context: encrypt(formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd HH:mm:ss")),
      },
    });
    data = response.data;
    localStorage.setItem(`dsProcess-${uniqueId}`, JSON.stringify(data));
  } else {
    const cachedData = localStorage.getItem(`dsProcess-${uniqueId}`);
    if (cachedData) {
      data = JSON.parse(cachedData);
    }
  }

  return data;
};

export const registerCode = async (values: {
  uniqueId: string;
  code: string;
  deviceName: string;
}): Promise<
  AxiosResponse<{
    isSuccess: boolean;
    data?: string;
    contentGroup?: {
      id: string;
      viewport_size: string;
      layout_config: string;
    };
  }>
> => {
  const formData = new FormData();
  formData.append("Code", values.code);
  formData.append("Key", settings.PublicKey);
  formData.append("UniqueId", values.uniqueId);
  formData.append("LicenceType", "1");
  formData.append("DeviceId", navigator.hardwareConcurrency?.toString());
  formData.append("Manufacturer", navigator.appName ?? "");
  formData.append("DeviceName", values.deviceName);
  formData.append("VersionString", navigator.appVersion ?? "");
  formData.append("Platform", navigator.platform ?? "");
  formData.append("Idiom", navigator.language ?? "");
  formData.append("DeviceType", "WEB");

  try {
    const ipResponse = await axios.get("https://api.ipify.org?format=json");
    formData.append("IPAddress", ipResponse.data.ip);
  } catch (error) {
    formData.append("IPAddress", "-");
  }

  return dsApi.post(API_ENDPOINTS.PLAYER.REGISTER_CODE, formData, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      context: encrypt(formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd HH:mm:ss")),
    },
  });
};

// Get player sync status or create a new player sync if no tempSessionCode is provided
export const getPlayerSyncStatus = (tempSessionCode?: string): Promise<AxiosResponse<PlayerSyncStatus>> => {
  const formData = new FormData();
  if (tempSessionCode) {
    formData.append("tempSessionCode", tempSessionCode);
  }
  return dsApi.post(API_ENDPOINTS.PLAYER.PLAYER_SYNC_STATUS, formData, axiosConfig);
};

export const sendPlayerStat = (values: {
  uniqueId: string;
  code: string;
  contentId: number;
}): Promise<AxiosResponse<PlayerSyncStatus>> => {
  const formData = new FormData();
  formData.append("UniqueId", values.uniqueId);
  formData.append("Code", values.code);
  formData.append("ContentId", values.contentId.toString());

  return dsApi.post(API_ENDPOINTS.DS.SEND_PLAYER_STAT, formData, {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
      context: encrypt(formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd HH:mm:ss")),
    },
  });
};
